import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WALL_TYPE } from '@cx-sdk/constants';
import Icon from '@cx-web/components/primitives/icon';
import { type RequestContext } from '@cx-web/domain-types';
import { type Subscription } from '@cx-web/domain-types/myoc';
import Betslip from '@cx-web/feature-betslip/components/betslip';
import MiniBetslip from '@cx-web/feature-betslip/components/components/mini-betslip';
import miniBetslipStyles from '@cx-web/feature-betslip/components/style/mini-betslip.module.css';
import { useBetslipContext } from '@cx-web/feature-betslip/context';
import { useLabelActions } from '@cx-web/feature-i18n';
import { useMyOCUser } from '@cx-web/feature-myoc';
import { type RepubIs } from '@cx-web/utils';
import { pollForWindowProperty } from '@cx-web/utils/pollForWindowObject';
import classNames from 'classnames';
import { Menu } from 'lucide-react';
import { ClientOnly } from 'remix-utils/client-only';

import { type Country, type Repub } from '@cx-modules/types';

import Authentication from '~/apps/product-plus/components/Authentication';
import {
  Dialog as AuthDialog,
  DialogContent,
  DialogTitle,
} from '~/apps/product-plus/components/Authentication/components/Dialog';
import { useCurrentPath } from '~/apps/product-plus/hooks/use-current-path';
import { createPositiveEvConfig } from '~/apps/product-plus/pages/config';
import { useAuthModal, VIEW_VERIFY_PAYMENT } from '~/apps/product-plus/providers/auth-modal-provider';

import { Button, Dialog } from '../cxy/cxy.ts';

import styles from './styles/Header.module.css';
import { HeaderItems } from './HeaderItems.tsx';
import { MenuList } from './MenuList.tsx';

/** Overlay that covers the main content when betslip is open on GZ */
const GzBetslipOverlay = () => {
  const { open, toggleBetslip } = useBetslipContext();
  if (!open) return null;
  return <div className={styles.gzBetslipOverlay} onClick={() => toggleBetslip(false)} />;
};

export interface HeaderProps {
  BETSLIP_SCRIPT_URL: string;
  apiKey: string;
  baseUrl: string;
  countryCode: Country;
  currentUrl: string;
  deviceId: string;
  googleID: string;
  isTipstersEnabled: boolean;
  prosperStackUrl: string;
  repub: Repub;
  repubIs: RepubIs;
  repubPath: string;
  requestContext: RequestContext;
  resourcesUrl: string;
  subscriptions?: Subscription[];
}

export const Header = ({
  BETSLIP_SCRIPT_URL,
  apiKey,
  baseUrl,
  countryCode,
  currentUrl,
  deviceId,
  googleID,
  isTipstersEnabled,
  prosperStackUrl,
  repub,
  repubIs,
  repubPath,
  requestContext,
  resourcesUrl,
  subscriptions,
}: HeaderProps) => {
  const currentPath = useCurrentPath();
  const { userOddsFormat } = useBetslipContext();
  const currentPathWithRepub = `${repubPath}${currentPath}`;
  const labelActions = useLabelActions();
  const { t } = useTranslation();
  const { myocData } = useMyOCUser();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  const { isOpen, toggle, view } = useAuthModal();
  const config = createPositiveEvConfig(requestContext as RequestContext);
  const isPaywalled = config.isWalled && config.wallType === WALL_TYPE.PAYWALL;
  const isVerifyingPayment = view === VIEW_VERIFY_PAYMENT;

  const [betCount, setBetCount] = useState(0);

  useEffect(() => {
    if (!repubIs.OC) return;
    window.oc = {
      betslipAdvertActive: false,
    };

    let xpId = '';
    if (typeof window.xtremepush !== 'undefined') {
      const xpDeviceInfo = window.xtremepush('get', 'device_info');
      if (typeof xpDeviceInfo !== 'undefined') {
        xpId = xpDeviceInfo.id;
      }
    }

    const loadBetslip = async () => {
      const script = document.createElement('script');
      script.src = BETSLIP_SCRIPT_URL;
      script.type = 'text/javascript';
      script.async = true;
      document.head.appendChild(script);

      try {
        await pollForWindowProperty('ocRenderers.integrated-betslip');
      } catch {
        console.warn('OC Betslip renderer not present');
        return;
      }

      const initBetslip = window.ocRenderers?.['integrated-betslip'];

      initBetslip({
        apiKey,
        deviceId,
        oddsFormat: userOddsFormat,
        repub,
        xpId,
      });

      try {
        await pollForWindowProperty('ocBetslip');
        const betslip = window.ocBetslip;
        betslip.setCallbacks({
          setBetCount: () => setBetCount(betslip.getBetCount()),
        });
        setBetCount(window.ocBetslip.getBetCount());
      } catch {
        console.warn('OC Betslip not loaded');
      }
    };

    loadBetslip();
  }, [BETSLIP_SCRIPT_URL, apiKey, userOddsFormat, repub, repubIs, deviceId]);

  return (
    <>
      <header className={styles.header} data-testid="oc-plus-header">
        <a className={styles.isVisiblyHidden} href={`${currentPathWithRepub}#main`}>
          Skip to main content
        </a>

        <Dialog
          className={styles.scroll}
          isFullScreen
          isOpen={isMenuOpen}
          setIsOpen={setIsMenuOpen}
          title="navigation"
          triggerRef={ref}
        >
          <div style={{ width: '100%' }}>
            <h3 className={styles.mobileMenuTitle}>{!repubIs.GZ ? t('CX_PLUS_MENU') : t('TOOLS')}</h3>
            <HeaderItems
              baseUrl={baseUrl}
              currentPathWithRepub={currentPathWithRepub}
              isTipstersEnabled={isTipstersEnabled}
              repub={repub}
              resourcesUrl={resourcesUrl}
            />
          </div>
        </Dialog>

        <nav aria-label="Main" className={styles.navigation}>
          <Dialog.Trigger
            aria-label="Toggle navigation Menu"
            // @ts-ignore
            className={styles.menuButton}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            ref={ref}
          >
            {!repubIs.GZ ? <Menu /> : <span className={styles.menuButtonGz}>{t('TOOLS')}</span>}
          </Dialog.Trigger>

          <HeaderItems
            baseUrl={baseUrl}
            currentPathWithRepub={currentPathWithRepub}
            isTipstersEnabled={isTipstersEnabled}
            repub={repub}
            resourcesUrl={resourcesUrl}
          />

          <div className={styles.userButtons}>
            {!repubIs.GZ && (
              <MenuList
                isLoggedIn={!!myocData?.myOCUser}
                prosperStackUrl={prosperStackUrl}
                repub={repub}
                subscriptions={subscriptions}
              />
            )}
            {!repubIs.WS && (
              <Button
                aria-label="toggle betslip"
                className={styles.betslipButton}
                variant="outline"
                {...(repubIs.OC && {
                  onClick: () => {
                    window.ocBetslip.toggle();
                  },
                })}
              >
                {repubIs.OC ? (
                  <div className={miniBetslipStyles.wrapper} data-testid="betslip-button">
                    <div className={classNames(miniBetslipStyles.miniBetslip, styles.betslipMini)}>
                      <Icon height={20} icon="Betslip" width={20} />
                      {betCount > 0 && (
                        <span
                          className={[miniBetslipStyles.betCount, betCount > 0 ? miniBetslipStyles.betAdded : ''].join(
                            ' ',
                          )}
                        >
                          {betCount}
                        </span>
                      )}
                    </div>
                    <span className={miniBetslipStyles.betslipText}>Betslip</span>
                  </div>
                ) : (
                  <ClientOnly fallback={null}>
                    {() => (
                      <MiniBetslip
                        betslipTitle={labelActions.get('RESPONSIVE_BETSLIP')}
                        className={styles.betslipMini}
                      />
                    )}
                  </ClientOnly>
                )}
              </Button>
            )}
          </div>
        </nav>

        <ClientOnly fallback={null}>
          {() => <>{(repubIs.US || repubIs.GZ) && <Betslip className={styles.betslip} country={countryCode} />}</>}
        </ClientOnly>
        {repubIs.GZ && <GzBetslipOverlay />}
      </header>

      <AuthDialog onOpenChange={toggle} open={isOpen}>
        <DialogContent aria-describedby={undefined}>
          <DialogTitle style={{ display: 'none' }}>Log in or Sign up</DialogTitle>
          <Authentication>
            <Authentication.InfoSection resourcesUrl={resourcesUrl} view={view}>
              {isPaywalled && !isVerifyingPayment && <Authentication.InfoSection.InfoLink />}
            </Authentication.InfoSection>
            <Authentication.AuthSection currentUrl={currentUrl} googleID={googleID} view={view} />
          </Authentication>
        </DialogContent>
      </AuthDialog>
    </>
  );
};
